import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext, useSearchParams } from 'react-router';
import { organizationQuery, qk } from '@/api/queries';
import type { Website } from '@/api/types';
import { Button, Icon, Segmented, ThumbPaletteProvider, useToast, type IconName } from '@/design-system';
import { cx } from '@/lib/cx';
import { sectionTypesQuery } from '@/features/library/api';
import { errorText } from '@/shell/errorText';
import { LoadError } from '@/shell/LoadError';
import { patchWebsite, preferencesQuery, saveStructure, type PinnedPreset, type StructureMode } from './api';
import { DEFAULT_FILTERS, type GalleryFilters } from './Gallery';
import { AiMode } from './AiMode';
import { Constructor } from './Constructor';
import { GenerateBar, useGenerate } from './GenerateBar';
import { usePrefSummary, useSitePalette, useStructureLabels } from './labels';
import { ListMode } from './ListMode';
import { PageStack } from './PageStack';
import { PrefsButton, type StructureCtx } from './parts';
import { PreferencesDrawer } from './PreferencesDrawer';
import { leadsWith, pinLead, PromptMode, type PromptChoice } from './PromptMode';
import { blankSection, insertSection, MAX_PINS, toServer, updateAt, useStructureDoc, type StructureDoc } from './useStructure';
import s from './structure.module.css';

/** The design's three modes, then «Из промпта библиотеки» (P6-X01, additive; a view of the tab, not a stored mode). */
type Mode = StructureMode | 'prompt';
const MODES: { key: Mode; icon: IconName; no: string }[] = [
  { key: 'list', icon: 'list-checks', no: '01' },
  { key: 'canvas', icon: 'square-plus', no: '02' },
  { key: 'ai', icon: 'sparkles', no: '03' },
  { key: 'prompt', icon: 'scroll-text', no: '04' },
];

/**
 * /sites/:id/structure — «Структура» (DESIGN_SPEC §4.6): the three numbered mode cards, the toolbar
 * (site type, «Предпочтения для AI», the counts), the mode's content and the sticky generate bar.
 * Every thumbnail below paints with the site's preference palette.
 */
export function StructureTab() {
  const site = useOutletContext<Website>();
  const { t } = useTranslation('structure');
  const { t: tc } = useTranslation('common');
  const { t: tl } = useTranslation('library');
  const toast = useToast();
  const qc = useQueryClient();
  const org = useQuery(organizationQuery(site.organization.id)).data;
  const prefs = useQuery(preferencesQuery(site.id)).data;
  const types = useQuery(sectionTypesQuery).data;
  const onSaveError = useCallback((e: unknown) => toast(t('save_error', { error: errorText(e, tc) })), [toast, t, tc]);
  const doc = useStructureDoc(site.id, onSaveError);
  const [selected, setSelected] = useState(1);
  const [filters, setFilters] = useState<GalleryFilters>(DEFAULT_FILTERS);
  const palette = useSitePalette(prefs, org?.brand_colors);
  const summary = usePrefSummary(prefs, site.organization.brand_name, palette);
  // «Из промпта библиотеки» is `?mode=prompt` (the website's stored modes are the design's three).
  const [params, setParams] = useSearchParams();
  const fromPrompt = params.get('mode') === 'prompt';
  const mode: Mode = fromPrompt ? 'prompt' : site.structure_mode;

  const setMode = useCallback(
    (m: Mode) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (m === 'prompt') next.set('mode', 'prompt');
          else next.delete('mode');
          return next;
        },
        { replace: true },
      );
      if (m === 'prompt' || m === qc.getQueryData<Website>(qk.website(site.id))?.structure_mode) return;
      patchWebsite(qc, site.id, { structure_mode: m }).catch((e: unknown) => toast(errorText(e, tc)));
    },
    [qc, site.id, toast, tc, setParams],
  );
  const onPinned = useCallback(
    (i: number) => {
      setSelected(i);
      setMode('list');
    },
    [setMode],
  );
  useLibraryPin(site, doc, onPinned);
  const sel = Math.min(selected, Math.max(0, doc.secs.length - 1));
  const [cz, setCz] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const { generate, busy: generating } = useGenerate(site, doc);
  const [choice, setChoice] = useState<PromptChoice>(() => ({ preset: null, direction: t('prompt.direction_default', { brand: site.organization.brand_name }) }));
  // P6-X01: pin the chosen prompt as the lead section, then generate with the art direction as the `prompt`.
  const generateFromPrompt = useCallback(async () => {
    if (!choice.preset) {
      toast(t('prompt.pick_first'));
      return;
    }
    if (!leadsWith(doc.secs, choice.preset)) {
      const next = pinLead(doc.secs, choice.preset);
      if (!next) {
        toast(t('prompt.no_version'));
        return;
      }
      doc.update(() => next);
    }
    await generate(choice.direction.trim() || null);
  }, [choice, doc, generate, toast, t]);
  const ctx: StructureCtx = {
    site,
    doc,
    selected: sel,
    select: setSelected,
    filters,
    setFilters,
    openConstructor: () => setCz(true),
    openBlank: () => {
      doc.update(() => []);
      setCz(true);
    },
  };
  const libraryTotal = types?.reduce((n, x) => n + x.presets_count, 0) ?? 0;
  const libraryLabel = t('library', { count: libraryTotal });

  if (doc.error && !doc.secs.length) return <LoadError error={doc.error} onRetry={doc.retry} />;

  return (
    <ThumbPaletteProvider palette={palette} customLabel={tl('custom_block')}>
      <div className={s.modes} role="radiogroup" aria-label={t('modes_label')}>
        {MODES.map((m) => (
          <button
            key={m.key}
            type="button"
            role="radio"
            aria-checked={mode === m.key}
            className={cx(s.mode, m.key === 'prompt' && s.modeExtra)}
            data-mode={m.key}
            onClick={() => setMode(m.key)}
          >
            <span className={s.modeIcon}>
              <Icon name={m.icon} size={19} />
            </span>
            <span className={s.modeText}>
              <span className={s.modeName}>
                {t(`modes.${m.key}.name`)}
                <span className={s.modeNo}>{m.no}</span>
              </span>
              <span className={s.modeDesc}>{t(`modes.${m.key}.desc`)}</span>
            </span>
          </button>
        ))}
      </div>

      <div className={s.toolbar}>
        <Segmented
          variant="compact"
          className={s.typeSeg}
          label={t('type_label')}
          value={site.type}
          options={(['ecommerce', 'landing'] as const).map((k) => ({ value: k, label: t(`type.${k}`) }))}
          onChange={(type) =>
            patchWebsite(qc, site.id, { type })
              .then(() => toast(t('type_changed', { type: t(`type.${type}`) })))
              .catch((e: unknown) => toast(errorText(e, tc)))
          }
        />
        <PrefsButton summary={summary} onClick={() => setPrefsOpen(true)} />
        {/* P6-X01 below 1560 px: the 4th mode as a toolbar pill, so the design's three cards keep their width. */}
        <button type="button" className={s.promptPill} aria-pressed={mode === 'prompt'} data-mode="prompt-pill" onClick={() => setMode(mode === 'prompt' ? site.structure_mode : 'prompt')}>
          <Icon name="scroll-text" size={15} className={s.prefsIcon} />
          <span className={s.nowrap}>{t('modes.prompt.name')}</span>
          <span className={s.modeNo}>04</span>
        </button>
        <span className={s.counts}>
          {t('counts', { sections: t('sections', { count: doc.secs.length }), library: libraryLabel })}
        </span>
      </div>

      {mode === 'prompt' ? (
        <PromptMode {...ctx} choice={choice} setChoice={setChoice} onGenerate={() => void generateFromPrompt()} busy={generating} />
      ) : mode === 'canvas' ? (
        <CanvasMode {...ctx} />
      ) : mode === 'ai' ? (
        <AiMode
          {...ctx}
          libraryLabel={libraryLabel}
          onReplace={(i) => {
            setSelected(i);
            setFilters({ ...filters, layout: 'all' });
            setMode('list');
          }}
        />
      ) : (
        <ListMode {...ctx} />
      )}

      <GenerateBar site={site} doc={doc} summary={summary.summary} onGenerate={mode === 'prompt' ? () => void generateFromPrompt() : undefined} />

      {cz && (
        <Constructor
          site={site}
          doc={doc}
          summary={summary}
          filters={filters}
          setFilters={setFilters}
          onClose={() => setCz(false)}
          onPrefs={() => setPrefsOpen(true)}
          onGenerate={() => {
            setCz(false);
            void generate();
          }}
        />
      )}

      <PreferencesDrawer siteId={site.id} prefs={prefs} brandName={site.organization.brand_name} brandColors={org?.brand_colors} open={prefsOpen} onClose={() => setPrefsOpen(false)} />
    </ThumbPaletteProvider>
  );
}

/**
 * The Design Library's «Добавить в структуру» hands over `?section=<type>&pin=<version id>` (P3-E02):
 * the version becomes the primary design of the first section of that type (a new one before the
 * footer when there is none), saved at once; the toast says the version is pinned (TZ §7.2).
 */
function useLibraryPin(site: Website, doc: StructureDoc, onPinned: (i: number) => void) {
  const { t } = useTranslation('structure');
  const { t: tc } = useTranslation('common');
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const { designName } = useStructureLabels();
  const done = useRef<string | null>(null);
  const type = params.get('section');
  const pin = params.get('pin');
  const ready = !doc.loading && !doc.error;
  useEffect(() => {
    if (!type || !pin || !ready || done.current === pin) return;
    done.current = pin;
    let secs = doc.secs;
    let i = secs.findIndex((x) => x.section_type === type);
    if (i < 0) {
      secs = insertSection(secs, blankSection(type));
      i = secs.findIndex((x) => x.section_type === type);
    }
    // Only the version id matters for the save; the answer carries the rest.
    const ref = { preset_version_id: pin } as PinnedPreset;
    secs = updateAt(secs, i, (x) => ({ ...x, presets: [ref, ...x.presets.filter((p) => p.preset_version_id !== pin)].slice(0, MAX_PINS) }));
    const at = i;
    void doc
      .flush()
      .then(() => saveStructure(site.id, toServer(secs)))
      .then((out) => {
        doc.adopt(out);
        onPinned(at);
        const p = out.sections[at]?.presets[0];
        if (p) toast(t('toast.pinned', { design: designName(p), version: p.version }));
      })
      .catch((e: unknown) => toast(errorText(e, tc)))
      .finally(() =>
        setParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete('section');
            next.delete('pin');
            return next;
          },
          { replace: true },
        ),
      );
  }, [type, pin, ready, doc, site.id, setParams, onPinned, toast, t, tc, designName]);
}

/** Конструктор: the entry card (open with the current structure, or a blank page) and the page preview. */
function CanvasMode({ doc, select, openConstructor, openBlank }: StructureCtx) {
  const { t } = useTranslation('structure');
  return (
    <div className={s.canvasGrid}>
      <div className={s.canvasIntro}>
        <span className={s.canvasIcon}>
          <Icon name="square-plus" size={24} />
        </span>
        <div className={s.canvasText}>
          <h2 className={s.canvasTitle}>{t('canvas.title')}</h2>
          <span className={s.canvasLead}>{t('canvas.text')}</span>
        </div>
        <div className={s.row8}>
          <Button icon="maximize-2" iconSize={15} onClick={openConstructor} data-action="cz-open">
            {t('canvas.open_keep')}
          </Button>
          <Button variant="secondary" icon="file" iconSize={15} onClick={openBlank}>
            {t('canvas.open_blank')}
          </Button>
        </div>
        <span className={s.canvasHint}>{t('canvas.hint')}</span>
      </div>
      <div className={s.canvasStack}>
        <PageStack secs={doc.secs} onSelect={select} />
      </div>
    </div>
  );
}
